import { QueryClient } from '@tanstack/react-query';
import type {
  CaseWorkspaceResponse,
  CaseNoteResponse,
  AccountHoldingsView,
  HoldingView,
  OperationCase,
  OperationsRequestContext,
  PageResponse,
  QkvinWorkflowActivity,
  QkvinWorkflowResponse,
  TenantUser,
} from '../api/operations-service';
import { ACCESS_TOKEN_KEY, SESSION_KEY, workflowSteps } from './constants';
import { demoPage } from './demo-data';
import type {
  CaseClientAccount,
  CaseClientContext,
  CaseClientSubAccount,
  CaseClientWallet,
  CaseNote,
  KycAmlAccountBlock,
  KycAmlCase,
  KycAmlCheck,
  KycAmlClientAccount,
  KycAmlDocument,
  KycAmlFilters,
  KycAmlOverride,
  NavItem,
  OpsSession,
} from './types';

export function caseSummaryDraft(
  caseItem: OperationCase,
): Record<string, string> {
  const clientContext = caseClientContext(caseItem);
  return {
    title: caseItem.title ?? '',
    description: caseItem.description ?? '',
    priority: caseItem.priority ?? 'NORMAL',
    status: caseItem.status ?? 'NEW',
    assignedRole: caseItem.assignedRole ?? 'OPERATIONS',
    dueAt: caseItem.dueAt ?? '',
    customerName: caseItem.customerName ?? clientContext.legalName,
    customerNumber: caseItem.customerNumber ?? clientContext.clientReference,
    tradeReference: caseItem.tradeReference ?? '',
  };
}

export function caseClientContext(caseItem: OperationCase): CaseClientContext {
  const metadata = caseItem.metadata ?? {};
  const context = asRecord(getPath(metadata, 'clientContext'));
  const rawAccountData = firstRecord(
    getPath(metadata, 'accountData'),
    metadata,
  );
  const rawData = asRecord(getPath(rawAccountData, 'data'));
  const profile = firstRecord(
    getPath(context, 'clientProfile'),
    getPath(rawAccountData, 'data.clientProfile'),
    getPath(rawAccountData, 'clientProfile'),
    getPath(metadata, 'clientSnapshot.profile'),
    getPath(metadata, 'profile'),
    getPath(rawData, 'clientProfile'),
  );
  const party = firstRecord(
    getPath(metadata, 'clientSnapshot.party'),
    getPath(metadata, 'party'),
    getPath(rawData, 'party'),
  );
  const kyc = firstRecord(
    getPath(metadata, 'clientSnapshot.kycStatus'),
    getPath(metadata, 'kycAmlScreening.summary'),
    getPath(rawAccountData, 'kycStatus'),
  );
  const accounts = caseClientAccounts(
    firstList(
      getPath(context, 'accounts'),
      getPath(rawAccountData, 'data.portfolioView.accounts'),
      getPath(rawAccountData, 'portfolioView.accounts'),
      getPath(metadata, 'clientSnapshot.portfolio.accounts'),
      getPath(metadata, 'clientSnapshot.accounts.items'),
      getPath(metadata, 'portfolio.accounts'),
      getPath(rawData, 'portfolioView.accounts'),
      getPath(metadata, 'clientSnapshot.account'),
    ),
  );
  const wallets = caseClientWallets(
    firstList(
      getPath(context, 'wallets'),
      getPath(rawAccountData, 'data.portfolioView.wallets'),
      getPath(rawAccountData, 'portfolioView.wallets'),
      getPath(metadata, 'clientSnapshot.portfolio.wallets'),
      getPath(metadata, 'portfolio.wallets'),
      getPath(rawData, 'portfolioView.wallets'),
    ),
  );
  const accountId = firstText(
    getPath(context, 'accountId'),
    caseItem.accountId,
    firstAccountText(accounts, 'accountId'),
  );
  const selectedAccount =
    accounts.find((account) => account.accountId === accountId) ?? accounts[0];

  return {
    available: firstBoolean(
      getPath(context, 'available'),
      getPath(rawAccountData, 'available'),
      getPath(rawData, 'available'),
    ),
    restricted: firstBoolean(
      getPath(context, 'restricted'),
      getPath(rawAccountData, 'restricted'),
      getPath(rawData, 'restricted'),
    ),
    reason: firstText(
      getPath(context, 'reason'),
      getPath(rawAccountData, 'reason'),
      getPath(rawData, 'reason'),
    ),
    partyId: firstText(
      getPath(context, 'partyId'),
      caseItem.clientId,
      getPath(rawData, 'partyId'),
      getPath(profile, 'partyId'),
      getPath(party, 'partyId'),
      getPath(party, 'id'),
    ),
    clientReference: firstText(
      getPath(context, 'clientReference'),
      getPath(profile, 'clientReference'),
      getPath(profile, 'customerNumber'),
      getPath(party, 'clientReference'),
      getPath(party, 'customerNumber'),
      caseItem.customerNumber,
    ),
    legalName: firstText(
      getPath(context, 'legalName'),
      getPath(profile, 'legalName'),
      getPath(profile, 'fullName'),
      getPath(profile, 'name'),
      getPath(party, 'legalName'),
      getPath(party, 'fullName'),
      getPath(party, 'name'),
      [getPath(profile, 'givenName'), getPath(profile, 'familyName')]
        .filter(Boolean)
        .join(' '),
      [getPath(party, 'givenName'), getPath(party, 'familyName')]
        .filter(Boolean)
        .join(' '),
      caseItem.customerName,
    ),
    givenName: firstText(
      getPath(context, 'givenName'),
      getPath(profile, 'givenName'),
      getPath(profile, 'firstName'),
      getPath(party, 'givenName'),
      getPath(party, 'firstName'),
    ),
    familyName: firstText(
      getPath(context, 'familyName'),
      getPath(profile, 'familyName'),
      getPath(profile, 'lastName'),
      getPath(party, 'familyName'),
      getPath(party, 'lastName'),
    ),
    email: firstText(
      getPath(context, 'email'),
      getPath(profile, 'email'),
      getPath(profile, 'emailAddress'),
      getPath(party, 'email'),
      getPath(party, 'emailAddress'),
      getPath(metadata, 'email'),
    ),
    phoneNumber: firstText(
      getPath(context, 'phoneNumber'),
      getPath(profile, 'phoneNumber'),
      getPath(profile, 'mobile'),
      getPath(profile, 'phone'),
      getPath(party, 'phoneNumber'),
      getPath(party, 'mobile'),
      getPath(party, 'phone'),
      getPath(metadata, 'phoneNumber'),
    ),
    dateOfBirth: firstText(
      getPath(context, 'dateOfBirth'),
      getPath(profile, 'dateOfBirth'),
      getPath(party, 'dateOfBirth'),
    ),
    countryOfResidence: joinedText(
      getPath(context, 'nationalities'),
      getPath(profile, 'nationalities'),
      getPath(party, 'nationalities'),
      getPath(profile, 'countryOfResidence'),
      getPath(party, 'countryOfResidence'),
    ),
    taxResidency: joinedText(
      getPath(context, 'taxResidencies'),
      getPath(profile, 'taxResidencies'),
      getPath(party, 'taxResidencies'),
    ),
    nationalInsuranceNumberMasked: firstText(
      getPath(context, 'nationalInsuranceNumberMasked'),
      getPath(profile, 'nationalInsuranceNumberMasked'),
      getPath(profile, 'nationalInsuranceNumber'),
      getPath(party, 'nationalInsuranceNumberMasked'),
      getPath(party, 'nationalInsuranceNumber'),
    ),
    username: firstText(
      getPath(context, 'username'),
      getPath(profile, 'username'),
    ),
    twoFactorMethod: firstText(
      getPath(context, 'twoFactorMethod'),
      getPath(profile, 'twoFactorMethod'),
    ),
    twoFactorContactMasked: firstText(
      getPath(context, 'twoFactorContactMasked'),
      getPath(profile, 'twoFactorContactMasked'),
    ),
    twoFactorAppLabel: firstText(
      getPath(context, 'twoFactorAppLabel'),
      getPath(profile, 'twoFactorAppLabel'),
    ),
    twoFactorEnabled: firstBoolean(
      getPath(context, 'twoFactorEnabled'),
      getPath(profile, 'twoFactorEnabled'),
    ),
    twoFactorProvisioningRequired: firstBoolean(
      getPath(context, 'twoFactorProvisioningRequired'),
      getPath(profile, 'twoFactorProvisioningRequired'),
    ),
    passwordChangedAt: firstText(
      getPath(context, 'passwordChangedAt'),
      getPath(profile, 'passwordChangedAt'),
    ),
    passwordResetRequired: firstBoolean(
      getPath(context, 'passwordResetRequired'),
      getPath(profile, 'passwordResetRequired'),
    ),
    kycStatus: firstText(
      getPath(context, 'kycStatus'),
      getPath(profile, 'kycStatus'),
      getPath(kyc, 'kycStatus'),
      getPath(kyc, 'screeningStatus'),
    ),
    kycVerified: firstBoolean(
      getPath(context, 'kycVerified'),
      getPath(profile, 'kycVerified'),
      getPath(kyc, 'kycVerified'),
    ),
    kycDocumentsRequired: firstBoolean(
      getPath(context, 'kycDocumentsRequired'),
      getPath(profile, 'kycDocumentsRequired'),
      getPath(kyc, 'kycDocumentsRequired'),
    ),
    status: firstText(
      getPath(context, 'status'),
      getPath(profile, 'status'),
      getPath(party, 'status'),
    ),
    accountId,
    accountReference: firstText(
      getPath(context, 'accountReference'),
      selectedAccount?.accountReference,
    ),
    accountType: firstText(
      getPath(context, 'accountType'),
      selectedAccount?.accountType,
      caseItem.accountType,
    ),
    wrapperType: firstText(
      getPath(context, 'wrapperType'),
      selectedAccount?.wrapperType,
    ),
    accountStatus: firstText(
      getPath(context, 'accountStatus'),
      selectedAccount?.status,
    ),
    portfolioCurrency: firstText(
      getPath(context, 'portfolioCurrency'),
      firstAccountText(wallets, 'currencyCode'),
      firstAccountText(accounts, 'currencyCode'),
      'GBP',
    ),
    accounts,
    wallets,
  };
}

function caseClientAccounts(
  records: Record<string, unknown>[],
): CaseClientAccount[] {
  return records
    .map((record) => ({
      accountId: firstText(getPath(record, 'accountId'), getPath(record, 'id')),
      accountReference: firstText(
        getPath(record, 'accountReference'),
        getPath(record, 'accountNumber'),
        getPath(record, 'externalAccountId'),
      ),
      accountType: firstText(getPath(record, 'accountType')),
      wrapperType: firstText(getPath(record, 'wrapperType')),
      status: firstText(getPath(record, 'status')),
      openedAt: firstText(
        getPath(record, 'openedAt'),
        getPath(record, 'openedDate'),
        getPath(record, 'openingDate'),
        getPath(record, 'accountOpeningDate'),
        getPath(record, 'createdAt'),
        getPath(record, 'startDate'),
      ),
      closedAt: firstText(
        getPath(record, 'closedAt'),
        getPath(record, 'closedDate'),
        getPath(record, 'closingDate'),
        getPath(record, 'accountClosingDate'),
        getPath(record, 'closureDate'),
        getPath(record, 'terminatedAt'),
        getPath(record, 'endDate'),
      ),
      cashBalance: getPath(record, 'cashBalance'),
      reservedAmount: getPath(record, 'reservedAmount'),
      availableToTrade:
        getPath(record, 'availableToTrade') ??
        getPath(record, 'cashPosition.availableToTrade') ??
        getPath(record, 'availableCash'),
      availableToWithdraw:
        getPath(record, 'availableToWithdraw') ??
        getPath(record, 'cashPosition.availableToWithdraw') ??
        getPath(record, 'withdrawableCash'),
      marketValue:
        getPath(record, 'marketValue') ??
        getPath(record, 'latestValuations.0.marketValue'),
      currencyCode: firstText(
        getPath(record, 'currencyCode'),
        getPath(record, 'latestValuations.0.currencyCode'),
      ),
      valuationDate: firstText(
        getPath(record, 'valuationDate'),
        getPath(record, 'latestValuations.0.valuationDate'),
      ),
      description: firstText(
        getPath(record, 'description'),
        getPath(record, 'accountDescription'),
        getPath(record, 'productName'),
        getPath(record, 'name'),
      ),
      subAccounts: caseClientSubAccounts(
        firstList(getPath(record, 'subAccounts')),
      ),
    }))
    .filter((account) => account.accountId || account.accountReference);
}

function caseClientSubAccounts(
  records: Record<string, unknown>[],
): CaseClientSubAccount[] {
  return records
    .map((record) => ({
      subAccountId: firstText(
        getPath(record, 'subAccountId'),
        getPath(record, 'id'),
      ),
      subAccountReference: firstText(
        getPath(record, 'subAccountReference'),
        getPath(record, 'reference'),
      ),
      baseCurrency: firstText(
        getPath(record, 'baseCurrency'),
        getPath(record, 'currencyCode'),
      ),
      status: firstText(getPath(record, 'status')),
      cashBalance: getPath(record, 'cashBalance'),
      availableToTrade: getPath(record, 'availableToTrade'),
      marketValue:
        getPath(record, 'marketValue') ??
        getPath(record, 'latestValuations.0.marketValue'),
    }))
    .filter(
      (subAccount) => subAccount.subAccountId || subAccount.subAccountReference,
    );
}

function caseClientWallets(
  records: Record<string, unknown>[],
): CaseClientWallet[] {
  return records
    .map((record) => ({
      walletId: firstText(getPath(record, 'walletId'), getPath(record, 'id')),
      walletReference: firstText(
        getPath(record, 'walletReference'),
        getPath(record, 'reference'),
      ),
      currencyCode: firstText(
        getPath(record, 'currencyCode'),
        getPath(record, 'currency'),
      ),
      availableBalance: getPath(record, 'availableBalance'),
      ledgerBalance: getPath(record, 'ledgerBalance'),
      status: firstText(getPath(record, 'status')),
    }))
    .filter((wallet) => wallet.walletId || wallet.walletReference);
}

export function workflowStepIndex(status?: string): number {
  const normalized = (status ?? 'NEW').toUpperCase();
  if (['RESOLVED', 'RESPONDED', 'CLOSED', 'COMPLETED'].includes(normalized)) {
    return 4;
  }
  if (
    [
      'PENDING_APPROVAL',
      'PENDING_GUARDRAIL_CHECK',
      'RESPONSE_READY',
      'READY_FOR_REVIEW',
    ].includes(normalized)
  ) {
    return 3;
  }
  if (
    [
      'ASSIGNED_TO_AI',
      'AI_PROCESSING',
      'ASSIGNED_TO_HUMAN',
      'PENDING_HUMAN_REVIEW',
      'PENDING_CUSTOMER_INFO',
      'PENDING_EXTERNAL_PROVIDER',
      'PENDING_CUSTOMER',
      'IN_PROGRESS',
    ].includes(normalized)
  ) {
    return 2;
  }
  if (['TRIAGE_IN_PROGRESS', 'TRIAGED', 'OPEN'].includes(normalized)) {
    return 1;
  }
  return 0;
}

export function extractCaseNotes(caseItem: OperationCase): CaseNote[] {
  const rawNotes = caseItem.metadata?.caseNotes;
  if (!Array.isArray(rawNotes)) {
    return [];
  }
  return rawNotes
    .map((note, index) => ({
      noteId: firstText(getPath(note, 'noteId'), `note-${index}`),
      noteType: firstText(getPath(note, 'noteType'), 'Internal'),
      body: firstText(
        getPath(note, 'body'),
        getPath(note, 'note'),
        getPath(note, 'text'),
      ),
      authorId: firstText(getPath(note, 'authorId'), 'operations-user'),
      authorRole: firstText(getPath(note, 'authorRole'), 'OPS_AGENT'),
      createdAt: firstText(
        getPath(note, 'createdAt'),
        new Date(0).toISOString(),
      ),
      pinned: Boolean(getPath(note, 'pinned')),
    }))
    .filter((note) => note.body)
    .sort(
      (left, right) =>
        new Date(right.createdAt).getTime() -
        new Date(left.createdAt).getTime(),
    );
}

export function actorPayload(context: OperationsRequestContext) {
  return {
    actorId: context.actorId,
    actorRole: context.actorRole,
  };
}

export function updateCaseCaches(
  query: QueryClient,
  context: OperationsRequestContext,
  updatedCase: OperationCase,
) {
  query.setQueriesData<OperationCase | undefined>(
    { queryKey: ['operations-case-detail'] },
    (current) =>
      current?.caseId === updatedCase.caseId
        ? { ...current, ...updatedCase }
        : current,
  );
  query.setQueriesData<CaseWorkspaceResponse | undefined>(
    { queryKey: ['operations-case-workspace'] },
    (workspace) => {
      if (
        !workspace?.caseItem ||
        workspace.caseItem.caseId !== updatedCase.caseId
      ) {
        return workspace;
      }
      const caseItem = { ...workspace.caseItem, ...updatedCase };
      return { ...workspace, caseItem, notes: extractCaseNotes(caseItem) };
    },
  );
  query.setQueriesData<PageResponse<OperationCase>>(
    { queryKey: ['operations-cases'] },
    (page) => updatePageItem(page, 'caseId', updatedCase.caseId, updatedCase),
  );
  query.invalidateQueries({ queryKey: ['operations-dashboard', context] });
  query.invalidateQueries({
    queryKey: ['operations-dashboard-open-cases', context],
  });
}

export function canAccessNavItem(
  item: NavItem,
  actorRoles?: string[],
): boolean {
  const roles = (actorRoles ?? []).map(normalizeRoleClaim);
  if (roles.includes('ROOT')) {
    return item.key === 'platform';
  }
  if (item.key === 'platform') {
    return false;
  }
  if (!item.roles || !item.roles.length || roles.includes('ADMIN')) {
    return true;
  }
  return item.roles.some((role) => roles.includes(normalizeRoleClaim(role)));
}

export function sortedMetricEntries(
  data?: Record<string, number>,
): Array<[string, number]> {
  return Object.entries(data ?? {})
    .filter(([, value]) => Number.isFinite(value) && value > 0)
    .sort((left, right) => right[1] - left[1]);
}

export function sumMetricEntries(entries: Array<[string, number]>): number {
  return entries.reduce((sum, [, value]) => sum + value, 0);
}

export function countByValue<T>(
  items: T[],
  getValue: (item: T) => string,
): Record<string, number> {
  return items.reduce<Record<string, number>>((counts, item) => {
    const key = getValue(item) || 'UNKNOWN';
    counts[key] = (counts[key] ?? 0) + 1;
    return counts;
  }, {});
}

export function highPriority(value?: string): boolean {
  return ['HIGH', 'URGENT', 'REGULATORY_DEADLINE'].includes(
    (value ?? '').toUpperCase(),
  );
}

export function terminalCaseStatus(value?: string): boolean {
  return ['CLOSED', 'COMPLETED', 'CANCELLED', 'RESOLVED', 'FAILED'].includes(
    (value ?? '').toUpperCase(),
  );
}

export function isPastDue(value?: string): boolean {
  const time = dateValue(value);
  return time != null && time < Date.now();
}

export function isDueSoon(value?: string): boolean {
  const time = dateValue(value);
  if (time == null) {
    return false;
  }
  const now = Date.now();
  return time >= now && time <= now + 24 * 60 * 60 * 1000;
}

export function dateValue(value?: string): number | null {
  if (!value) {
    return null;
  }
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? null : time;
}

export function compareOperationalRisk(
  left: OperationCase,
  right: OperationCase,
): number {
  const leftDue =
    dateValue(left.slaDueAt ?? left.dueAt) ?? Number.MAX_SAFE_INTEGER;
  const rightDue =
    dateValue(right.slaDueAt ?? right.dueAt) ?? Number.MAX_SAFE_INTEGER;
  const leftRisk =
    (isPastDue(left.slaDueAt ?? left.dueAt) ? 100 : 0) +
    priorityRank(left.priority);
  const rightRisk =
    (isPastDue(right.slaDueAt ?? right.dueAt) ? 100 : 0) +
    priorityRank(right.priority);
  return rightRisk - leftRisk || leftDue - rightDue;
}

export function priorityRank(value?: string): number {
  const normalized = (value ?? '').toUpperCase();
  if (normalized === 'URGENT' || normalized === 'REGULATORY_DEADLINE') return 4;
  if (normalized === 'HIGH') return 3;
  if (normalized === 'NORMAL') return 2;
  if (normalized === 'LOW') return 1;
  return 0;
}

export function quinSearchPayload(
  filters: KycAmlFilters,
  pageIndex = 0,
  pageSize = 10,
) {
  const query = filters.q.trim();
  const fields: Record<string, unknown>[] = [];
  if (filters.riskLevel) {
    fields.push({
      field: 'riskProfile',
      value: filters.riskLevel,
      operator: 'EQUALS',
      condition: 'AND',
    });
  }
  if (filters.workflowStage) {
    fields.push({
      field: 'dueDiligenceType',
      value: filters.workflowStage,
      operator: 'EQUALS',
      condition: 'AND',
    });
  }
  return {
    pageNo: pageIndex + 1,
    recordsPerPage: pageSize,
    userId: '',
    sortBy: 'createdDate',
    sortDirection: 'DESC',
    searchParams: {
      caseStatus: filters.status || null,
      caseNumber: query,
      clientNumber: '',
      startDate: null,
      endDate: null,
    },
    fields,
  };
}

export function mapQuinCaseToKycAmlCase(
  source: Record<string, unknown>,
): KycAmlCase {
  const caseId = firstText(
    getPath(source, 'caseId'),
    getPath(source, 'id'),
    getPath(source, 'case.id'),
    randomDemoId(),
  );
  const clientId = firstText(
    getPath(source, 'clientId'),
    getPath(source, 'client.id'),
    getPath(source, 'partyId'),
  );
  const caseReference = firstText(
    getPath(source, 'caseNumber'),
    getPath(source, 'caseReference'),
    getPath(source, 'reference'),
    shortId(caseId),
  );
  const riskLevel = firstText(
    getPath(source, 'overAllRisk'),
    getPath(source, 'riskLevel'),
    getPath(source, 'riskRating'),
    getPath(source, 'riskProfile'),
    getPath(source, 'risk'),
    'MEDIUM',
  ).toUpperCase();
  const kycStatus = firstText(
    getPath(source, 'kycStatus'),
    getPath(source, 'kyc_status'),
    getPath(source, 'status'),
    getPath(source, 'caseStatus'),
    getPath(source, 'case_status'),
    'PENDING_REVIEW',
  ).toUpperCase();
  const caseStatus = firstText(
    getPath(source, 'caseStatus'),
    getPath(source, 'case_status'),
  ).toUpperCase();
  const caseSubStatus = firstText(
    getPath(source, 'caseSubStatus'),
    getPath(source, 'case_sub_status'),
    getPath(source, 'subStatus'),
  ).toUpperCase();

  return {
    caseId,
    caseReference,
    clientId,
    clientName: firstText(
      getPath(source, 'clientName'),
      getPath(source, 'caseName'),
      getPath(source, 'name'),
      getPath(source, 'client.fullName'),
      getPath(source, 'customerName'),
      'Client',
    ),
    clientNumber: firstText(
      getPath(source, 'clientNumber'),
      getPath(source, 'customerNumber'),
    ),
    accountId: firstText(
      getPath(source, 'accountId'),
      getPath(source, 'accountNumber'),
    ),
    reviewType: labelize(
      firstText(
        getPath(source, 'caseType'),
        getPath(source, 'type'),
        getPath(source, 'reviewType'),
        'KYC review',
      ),
    ),
    riskLevel,
    kycStatus,
    caseStatus,
    caseSubStatus,
    amlStatus: firstText(
      getPath(source, 'amlStatus'),
      getPath(source, 'screeningStatus'),
      riskLevel,
      'UNKNOWN',
    ).toUpperCase(),
    onboardingStatus: firstText(
      getPath(source, 'onboardingStatus'),
      getPath(source, 'clientStatus'),
      'UNKNOWN',
    ).toUpperCase(),
    dueDiligenceStatus: firstText(
      getPath(source, 'dueDiligenceStatus'),
      getPath(source, 'dueDiligenceType'),
      getPath(source, 'ddStatus'),
      getPath(source, 'riskReviewStatus'),
      'STANDARD_DUE_DILIGENCE',
    ).toUpperCase(),
    workflowStage: labelize(
      firstText(
        getPath(source, 'workflowStage'),
        getPath(source, 'stage'),
        getPath(source, 'caseStage'),
        kycStatus,
      ),
    ),
    approvalStage: kycApprovalStage(
      kycStatus,
      caseStatus,
      caseSubStatus,
      firstText(
        getPath(source, 'approvalStage'),
        getPath(source, 'authorizationStatus'),
        getPath(source, 'authorisationStatus'),
        caseSubStatus || kycStatus,
      ),
    ),
    assignedTo: firstText(
      getPath(source, 'assignedTo'),
      getPath(source, 'assignedCaseWorkerName'),
      getPath(source, 'caseWorkerName'),
      getPath(source, 'workerName'),
    ),
    assignedRole: firstText(
      getPath(source, 'assignedRole'),
      getPath(source, 'assignedCaseWorkerUserRole'),
      getPath(source, 'role'),
      'COMPLIANCE_USER',
    ),
    dueAt: firstText(
      getPath(source, 'dueAt'),
      getPath(source, 'dueDate'),
      getPath(source, 'slaDueAt'),
      getPath(source, 'deadline'),
    ),
    lastUpdatedAt: firstText(
      getPath(source, 'updatedAt'),
      getPath(source, 'updatedDate'),
      getPath(source, 'lastUpdatedAt'),
      getPath(source, 'createdDate'),
    ),
    handlerType: firstText(
      getPath(source, 'handlerType'),
      getPath(source, 'reviewMode'),
      'HYBRID',
    ),
    checks: mapQuinChecksToKycChecks(
      getPath(source, 'checks') ?? getPath(source, 'screeningResults'),
    ),
    documents: mapQuinDocumentsToKycDocuments(
      getPath(source, 'documents') ?? getPath(source, 'uploadedDocuments'),
    ),
    overrides: mapQuinOverrides(
      getPath(source, 'overrides') ?? getPath(source, 'riskOverrides'),
    ),
    accountBlocks: mapQuinAccountBlocks(
      getPath(source, 'accountBlocks') ?? getPath(source, 'blocks'),
    ),
    flags: normaliseList<string>(
      getPath(source, 'flags') ?? getPath(source, 'riskFlags'),
    ),
    notes: firstText(
      getPath(source, 'notes'),
      getPath(source, 'comment'),
      getPath(source, 'summary'),
      caseSubStatus,
    ),
    orgName: firstText(getPath(source, 'orgName')),
    clientType: firstText(getPath(source, 'clientType')),
    dateOfBirth: firstText(getPath(source, 'dateOfBirth')),
    incorporatedOn: firstText(getPath(source, 'incorporatedOn')),
    country: firstText(getPath(source, 'country')),
    jurisdiction: firstText(getPath(source, 'jurisdiction')),
    createdAt: firstText(
      getPath(source, 'createdDate'),
      getPath(source, 'createdAt'),
    ),
    createdBy: firstText(
      getPath(source, 'createdByName'),
      getPath(source, 'createdBy'),
    ),
    updatedBy: firstText(
      getPath(source, 'updatedByName'),
      getPath(source, 'updatedBy'),
    ),
    authorisedAt: firstText(
      getPath(source, 'authorisedTime'),
      getPath(source, 'authorizedTime'),
    ),
    authorizerName: firstText(
      getPath(source, 'authorizerName'),
      getPath(source, 'authorisedByName'),
    ),
    reviewed: Boolean(getPath(source, 'isReviewed')),
    reviewHistories: normaliseList<Record<string, unknown>>(
      getPath(source, 'reviewHistories'),
    ),
    commentHistories: normaliseList<Record<string, unknown>>(
      getPath(source, 'commentHistories'),
    ),
  };
}

export function kycApprovalStage(
  kycStatus?: string,
  caseStatus?: string,
  caseSubStatus?: string,
  fallback?: string,
): string {
  const kyc = firstText(kycStatus).toUpperCase();
  const status = firstText(caseStatus).toUpperCase();
  const subStatus = firstText(caseSubStatus).toUpperCase();

  if (kyc === 'KYC_PASS' && status === 'OPEN') {
    return 'Pending';
  }
  if (kyc === 'KYC_PASS' && status === 'CLOSED' && subStatus === 'APPROVED') {
    return 'Approved';
  }
  if (kyc === 'KYC_PASS' && status === 'CLOSED' && subStatus === 'REJECTED') {
    return 'Rejected';
  }
  return labelize(firstText(fallback, subStatus, kyc));
}

export function mapQuinChecksToKycChecks(data: unknown): KycAmlCheck[] {
  return normaliseList<Record<string, unknown>>(
    getPath(data, 'checks') ??
      getPath(data, 'items') ??
      getPath(data, 'screeningResults') ??
      data,
  ).map((item, index) => ({
    checkId: firstText(
      getPath(item, 'checkId'),
      getPath(item, 'id'),
      `check-${index}`,
    ),
    checkName: firstText(
      getPath(item, 'checkName'),
      getPath(item, 'activityName'),
      getPath(item, 'name'),
      getPath(item, 'type'),
    ),
    checkLabel: firstText(
      getPath(item, 'checkLabel'),
      getPath(item, 'activityLabel'),
    ),
    activityId: firstText(getPath(item, 'activityId')),
    stageName: firstText(getPath(item, 'stageName')),
    name: labelize(
      firstText(
        getPath(item, 'checkLabel'),
        getPath(item, 'activityLabel'),
        getPath(item, 'checkName'),
        getPath(item, 'activityName'),
        getPath(item, 'name'),
        getPath(item, 'type'),
        'KYC check',
      ),
    ),
    category: labelize(
      firstText(
        getPath(item, 'category'),
        getPath(item, 'checkType'),
        'Screening',
      ),
    ),
    status: firstText(
      getPath(item, 'status'),
      getPath(item, 'checkStatus'),
      getPath(item, 'activityStatus'),
      'UNKNOWN',
    ).toUpperCase(),
    result: firstText(
      getPath(item, 'result'),
      getPath(item, 'outcome'),
      getPath(item, 'riskResult'),
      getPath(item, 'riskLevel'),
      getPath(item, 'activitySubStatus'),
      'UNKNOWN',
    ).toUpperCase(),
    mode: mapMode(
      firstText(
        getPath(item, 'mode'),
        getPath(item, 'handlerType'),
        getPath(item, 'source'),
        getPath(item, 'activityType'),
        'AI',
      ),
    ),
    confidence: numericValue(
      getPath(item, 'confidence'),
      getPath(item, 'score'),
      getPath(item, 'matchPercentage'),
    ),
    lastRunAt: firstText(
      getPath(item, 'lastRunAt'),
      getPath(item, 'updatedAt'),
      getPath(item, 'createdAt'),
      getPath(item, 'activityEndDatetime'),
      getPath(item, 'activityStartDatetime'),
    ),
    detail: firstText(
      getPath(item, 'detail'),
      getPath(item, 'message'),
      getPath(item, 'comment'),
      getPath(item, 'errorMessage'),
      getPath(item, 'activityDetails.ERROR'),
    ),
    editable:
      typeof getPath(item, 'editable') === 'boolean'
        ? Boolean(getPath(item, 'editable'))
        : null,
    actionRequired: Boolean(getPath(item, 'actionRequired')),
    activityDetails: asRecord(getPath(item, 'activityDetails')),
  }));
}

export function mapQkvinWorkflowChecks(
  data: QkvinWorkflowResponse | undefined,
): KycAmlCheck[] {
  const stages = normaliseList<Record<string, unknown>>(
    getPath(data, 'stages'),
  );
  return stages.flatMap((stage) => {
    const stageName = firstText(getPath(stage, 'stageName'));
    const stageLabel = firstText(getPath(stage, 'stageLabel'), stageName);
    return normaliseList<QkvinWorkflowActivity>(
      getPath(stage, 'activities'),
    ).map((activity, index) => ({
      activityId: firstText(
        activity.activityId,
        `${stageName}-${activity.activityName}-${index}`,
      ),
      checkName: firstText(activity.activityName),
      checkLabel: firstText(activity.activityLabel),
      name: labelize(
        firstText(
          activity.activityLabel,
          activity.activityName,
          'Workflow activity',
        ),
      ),
      category: labelize(stageLabel),
      status: firstText(activity.activityStatus, 'UNKNOWN').toUpperCase(),
      result: firstText(
        activity.activitySubStatus,
        activity.activityStatus,
        'UNKNOWN',
      ).toUpperCase(),
      mode: mapMode(firstText(activity.activityType, 'AI')),
      lastRunAt: firstText(
        activity.activityEndDatetime,
        activity.activityStartDatetime,
      ),
      detail: firstText(getPath(activity.activityDetails, 'ERROR')),
      editable: activity.editable,
      actionRequired: Boolean(activity.actionRequired),
      activityDetails: activity.activityDetails ?? {},
    }));
  });
}

export function mapQuinDocumentsToKycDocuments(
  data: unknown,
): KycAmlDocument[] {
  const groupedDocuments = asRecord(
    getPath(data, 'documentReview.uploadedDocuments') ??
      getPath(data, 'uploadedDocuments'),
  );
  const groupedItems = Object.keys(groupedDocuments).length
    ? Object.entries(groupedDocuments).flatMap(([category, value]) =>
        normaliseList<Record<string, unknown>>(value).map((item) => ({
          ...item,
          category,
        })),
      )
    : [];
  const documents = groupedItems.length
    ? groupedItems
    : normaliseList<Record<string, unknown>>(
        getPath(data, 'documents') ??
          getPath(data, 'items') ??
          getPath(data, 'uploadedDocuments') ??
          data,
      );
  return documents.map((item, index) => ({
    documentId: firstText(
      getPath(item, 'documentId'),
      getPath(item, 'id'),
      `document-${index}`,
    ),
    documentName: firstText(
      getPath(item, 'documentName'),
      getPath(item, 'fileName'),
      getPath(item, 'name'),
    ),
    documentType: labelize(
      firstText(
        getPath(item, 'documentType'),
        getPath(item, 'type'),
        getPath(item, 'category'),
        'Document',
      ),
    ),
    documentSubType: labelize(
      firstText(getPath(item, 'documentSubType'), getPath(item, 'subType')),
    ),
    status: firstText(
      getPath(item, 'status'),
      getPath(item, 'documentStatus'),
      getPath(item, 'uploadedTime') ? 'RECEIVED' : 'UNKNOWN',
    ).toUpperCase(),
    reviewStatus: firstText(
      getPath(data, 'documentReview.isDocumentReviewed') === true
        ? 'REVIEWED'
        : '',
      getPath(item, 'reviewStatus'),
      getPath(item, 'documentReviewStatus'),
      getPath(item, 'review'),
      'PENDING_REVIEW',
    ).toUpperCase(),
    required: Boolean(getPath(item, 'required') ?? getPath(item, 'isRequired')),
    reviewer: firstText(
      getPath(item, 'reviewer'),
      getPath(item, 'reviewedBy'),
      getPath(item, 'reviewerName'),
    ),
    uploadedAt: firstText(
      getPath(item, 'uploadedTime'),
      getPath(item, 'uploadedAt'),
      getPath(item, 'createdAt'),
    ),
    dueAt: firstText(getPath(item, 'dueAt'), getPath(item, 'expiryDate')),
    size: Number(getPath(item, 'size') ?? 0),
  }));
}

export function mapQuinOverrides(data: unknown): KycAmlOverride[] {
  return normaliseList<Record<string, unknown>>(data).map((item, index) => ({
    overrideId: firstText(
      getPath(item, 'overrideId'),
      getPath(item, 'id'),
      `override-${index}`,
    ),
    type: labelize(
      firstText(
        getPath(item, 'type'),
        getPath(item, 'riskType'),
        getPath(item, 'overrideRiskReason'),
        'KYC override',
      ),
    ),
    status: firstText(
      getPath(item, 'status'),
      getPath(item, 'decisionStatus'),
      getPath(item, 'currentCheckStatus'),
      'APPLIED',
    ).toUpperCase(),
    requestedBy: firstText(
      getPath(item, 'requestedBy'),
      getPath(item, 'createdBy'),
      getPath(item, 'updatedByName'),
      '-',
    ),
    requestedAt: firstText(
      getPath(item, 'requestedAt'),
      getPath(item, 'createdAt'),
      getPath(item, 'updatedTime'),
    ),
    reason: firstText(
      getPath(item, 'reason'),
      getPath(item, 'comment'),
      getPath(item, 'rationale'),
    ),
    decision: firstText(
      getPath(item, 'decision'),
      getPath(item, 'outcome'),
      getPath(item, 'currentCheckStatus'),
    ),
  }));
}

export function mapQuinAccountBlocks(data: unknown): KycAmlAccountBlock[] {
  return normaliseList<Record<string, unknown>>(data).map((item, index) => ({
    blockId: firstText(
      getPath(item, 'blockId'),
      getPath(item, 'id'),
      `block-${index}`,
    ),
    accountId: firstText(
      getPath(item, 'accountId'),
      getPath(item, 'accountNumber'),
      '-',
    ),
    blockType: labelize(
      firstText(
        getPath(item, 'blockType'),
        getPath(item, 'type'),
        'Account block',
      ),
    ),
    status: firstText(getPath(item, 'status'), 'ACTIVE').toUpperCase(),
    source:
      mapMode(
        firstText(
          getPath(item, 'source'),
          getPath(item, 'handlerType'),
          'Manual',
        ),
      ) === 'AI'
        ? 'AI'
        : 'Manual',
    reason: firstText(getPath(item, 'reason'), getPath(item, 'comment'), '-'),
    createdAt: firstText(
      getPath(item, 'createdAt'),
      getPath(item, 'timestamp'),
    ),
  }));
}

export function mapInvestorAccountsToKycAccounts(
  data: unknown,
): KycAmlClientAccount[] {
  return normaliseList<Record<string, unknown>>(data).map(
    mapInvestorAccountToKycAccount,
  );
}

export function mapInvestorAccountToKycAccount(
  source: unknown,
  cashPosition?: unknown,
): KycAmlClientAccount {
  return {
    accountId: firstText(
      getPath(source, 'id'),
      getPath(source, 'accountId'),
      getPath(source, 'account_id'),
    ),
    partyId: firstText(
      getPath(source, 'partyId'),
      getPath(source, 'party_id'),
      getPath(source, 'clientId'),
    ),
    accountReference: firstText(
      getPath(source, 'accountReference'),
      getPath(source, 'accountNumber'),
      getPath(source, 'reference'),
    ),
    accountType: firstText(
      getPath(source, 'accountType'),
      getPath(source, 'type'),
    ),
    wrapperType: firstText(
      getPath(source, 'wrapperType'),
      getPath(source, 'wrapper'),
    ),
    status: firstText(getPath(source, 'status'), 'UNKNOWN').toUpperCase(),
    openedAt: firstText(
      getPath(source, 'openedAt'),
      getPath(source, 'openedDate'),
      getPath(source, 'openingDate'),
      getPath(source, 'accountOpeningDate'),
      getPath(source, 'createdAt'),
      getPath(source, 'startDate'),
    ),
    closedAt: firstText(
      getPath(source, 'closedAt'),
      getPath(source, 'closedDate'),
      getPath(source, 'closingDate'),
      getPath(source, 'accountClosingDate'),
      getPath(source, 'closureDate'),
      getPath(source, 'terminatedAt'),
      getPath(source, 'endDate'),
    ),
    cashBalance: numericValue(
      getPath(cashPosition, 'cashBalance'),
      getPath(cashPosition, 'availableToWithdraw'),
      getPath(source, 'cashBalance'),
      getPath(source, 'availableCash'),
      getPath(source, 'cashPosition.availableCash'),
    ),
    currency: firstText(
      getPath(cashPosition, 'currencyCode'),
      getPath(source, 'currency'),
      getPath(source, 'currencyCode'),
      'GBP',
    ),
  };
}

export function mapInvestorDocumentsToKycDocuments(
  data: unknown,
): KycAmlDocument[] {
  return normaliseList<Record<string, unknown>>(data).map((item, index) => ({
    documentId: firstText(
      getPath(item, 'id'),
      getPath(item, 'documentId'),
      `investor-document-${index}`,
    ),
    documentType: labelize(
      firstText(
        getPath(item, 'documentType'),
        getPath(item, 'fileName'),
        'Investor document',
      ),
    ),
    status: firstText(getPath(item, 'status'), 'AVAILABLE').toUpperCase(),
    reviewStatus: 'INVESTOR_RECORD',
    required: false,
    reviewer: 'Investor service',
    uploadedAt: firstText(
      getPath(item, 'createdAt'),
      getPath(item, 'updatedAt'),
    ),
  }));
}

export function mergeAccountBlocksFromAccounts(
  caseBlocks: KycAmlAccountBlock[],
  accounts: KycAmlClientAccount[],
): KycAmlAccountBlock[] {
  const blocksByAccount = new Map(
    caseBlocks.map((block) => [block.accountId, block]),
  );
  const investorBlocks = accounts
    .filter((account) => account.status === 'SUSPENDED')
    .map((account) => {
      const existing =
        blocksByAccount.get(account.accountId) ??
        blocksByAccount.get(account.accountReference ?? '');
      return {
        blockId: existing?.blockId ?? `status-${account.accountId}`,
        accountId: account.accountId,
        blockType: existing?.blockType ?? 'Account status hold',
        status: 'ACTIVE',
        source: existing?.source ?? 'Manual',
        reason:
          existing?.reason ??
          'Account status is SUSPENDED in investor service.',
        createdAt: existing?.createdAt ?? account.openedAt,
      } satisfies KycAmlAccountBlock;
    });
  const investorAccountIds = new Set(
    investorBlocks.map((block) => block.accountId),
  );
  return [
    ...investorBlocks,
    ...caseBlocks.filter((block) => !investorAccountIds.has(block.accountId)),
  ];
}

export function mapMode(value: string): 'AI' | 'Manual' | 'Hybrid' {
  const normalized = value.toUpperCase();
  if (normalized === 'HYBRID') return 'Hybrid';
  if (normalized === 'MANUAL' || normalized === 'HUMAN') return 'Manual';
  return 'AI';
}

export function numericValue(...values: unknown[]): number | undefined {
  for (const value of values) {
    const numeric = typeof value === 'number' ? value : Number(value);
    if (Number.isFinite(numeric)) {
      return numeric > 1 ? numeric / 100 : numeric;
    }
  }
  return undefined;
}

export function canSubmitKycDecision(actorRoles: string[]): boolean {
  return hasAnyRole(actorRoles, [
    'ADMIN',
    'MLRO',
    'COMPLIANCE_OFFICER',
    'COMPLIANCE_SUPERVISOR',
    'SUPERVISOR',
    'KYC_ANALYST',
    'KYC_COMPLIANCE_ANALYST',
    'KYC_MANAGER',
  ]);
}

export function canSupervisorApproveKycDecision(actorRoles: string[]): boolean {
  return hasAnyRole(actorRoles, [
    'ADMIN',
    'MLRO',
    'COMPLIANCE_SUPERVISOR',
    'SUPERVISOR',
    'KYC_SUPERVISOR',
    'KYC_MANAGER',
    'COMPLIANCE_MANAGER',
  ]);
}

export function hasAnyRole(
  actorRoles: string[],
  requiredRoles: string[],
): boolean {
  const roles = actorRoles.map((role) => role.toUpperCase());
  return requiredRoles.some(
    (role) =>
      roles.includes(role) ||
      (role === 'ADMIN' &&
        roles.some(
          (actorRole) => actorRole === 'ADMIN' || actorRole.endsWith('_ADMIN'),
        )),
  );
}

export function randomDemoId(): string {
  return (
    globalThis.crypto?.randomUUID?.() ??
    `demo-${Date.now()}-${Math.floor(Math.random() * 10000)}`
  );
}

export function addToPage<T>(
  page: PageResponse<T> | undefined,
  item: T,
): PageResponse<T> {
  const current = page ?? demoPage<T>([], 25);
  return {
    ...current,
    items: [item, ...current.items],
    total: current.total + 1,
  };
}

export function updatePageItem<
  T extends Record<string, unknown>,
  K extends keyof T,
>(
  page: PageResponse<T> | undefined,
  key: K,
  value: T[K] | undefined,
  patch: Partial<T>,
): PageResponse<T> | undefined {
  if (!page || value == null) {
    return page;
  }
  return {
    ...page,
    items: updateListItem(page.items, key, value, patch),
  };
}

export function updateListItem<
  T extends Record<string, unknown>,
  K extends keyof T,
>(items: T[], key: K, value: T[K] | undefined, patch: Partial<T>): T[] {
  if (value == null) {
    return items;
  }
  return items.map((item) =>
    item[key] === value ? { ...item, ...patch } : item,
  );
}

export function readSession(): OpsSession | null {
  const raw = sessionStorage.getItem(SESSION_KEY);
  if (!raw) {
    return null;
  }
  try {
    const session = JSON.parse(raw) as OpsSession;
    session.token =
      session.token || session.claimsToken || session.authToken || '';
    if (!session.token) {
      return null;
    }
    session.actorRoles = session.actorRoles?.length
      ? session.actorRoles
      : [session.actorRole].filter(Boolean);
    session.actorRole =
      primaryRole(session.actorRoles) || session.actorRole || 'OPS_MANAGER';
    sessionStorage.setItem(ACCESS_TOKEN_KEY, session.token);
    return session;
  } catch {
    return null;
  }
}

export function persistSession(session: OpsSession) {
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  sessionStorage.setItem(ACCESS_TOKEN_KEY, session.token);
}

export function extractAuthToken(data: unknown): string {
  const candidates = [
    data,
    getPath(data, 'response'),
    getPath(data, 'responseItem'),
    getPath(data, 'data'),
    getPath(data, 'responseItems.0'),
  ];
  for (const candidate of candidates) {
    const token = firstText(
      getPath(candidate, 'accessToken'),
      getPath(candidate, 'access_token'),
      getPath(candidate, 'token'),
    );
    if (token) {
      return token;
    }
  }
  return '';
}

export function extractClaimsToken(data: unknown): string {
  const candidates = [
    data,
    getPath(data, 'response'),
    getPath(data, 'responseItem'),
    getPath(data, 'data'),
    getPath(data, 'responseItems.0'),
  ];
  for (const candidate of candidates) {
    const token = firstText(
      getPath(candidate, 'idToken'),
      getPath(candidate, 'id_token'),
      getPath(candidate, 'accessToken'),
      getPath(candidate, 'access_token'),
      getPath(candidate, 'token'),
    );
    if (token && decodeJwt(token)) {
      return token;
    }
  }
  return '';
}

export function decodeJwt(token: string): Record<string, any> | null {
  try {
    const part = token.split('.')[1];
    if (!part) {
      return null;
    }
    return JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return null;
  }
}

export function buildSessionFromToken(
  claimsToken: string,
  fallbackUsername: string,
  authToken = claimsToken,
  fallbackClaims?: unknown,
): OpsSession {
  const claims = decodeJwt(claimsToken);
  if (!claims) {
    throw new Error(
      'Login returned an access token that could not be decoded.',
    );
  }

  const actorRoles = rolesFromClaims(claims, fallbackClaims);
  const tenantId = firstText(
    claims.tenant_id,
    claims['custom:tenant_id'],
    claims.tenantId,
    getPath(claims, 'tenant.id'),
    getPath(fallbackClaims, 'tenant_id'),
    getPath(fallbackClaims, 'custom:tenant_id'),
    getPath(fallbackClaims, 'tenantId'),
    getPath(fallbackClaims, 'tenant.id'),
  );
  const tenantCode = firstText(
    claims.tenant_code,
    claims['custom:tenant_code'],
    claims.tenantCode,
    getPath(claims, 'tenant.code'),
    getPath(fallbackClaims, 'tenant_code'),
    getPath(fallbackClaims, 'custom:tenant_code'),
    getPath(fallbackClaims, 'tenantCode'),
    getPath(fallbackClaims, 'tenant.code'),
  );

  if (!tenantId && !actorRoles.includes('ROOT')) {
    throw new Error(
      'Your operations JWT must include a tenant_id or custom:tenant_id claim.',
    );
  }

  return {
    token: claimsToken,
    authToken,
    claimsToken,
    username: firstText(
      claims.email,
      claims.username,
      claims['cognito:username'],
      getPath(fallbackClaims, 'username'),
      fallbackUsername,
      'Operations user',
    ),
    givenName: firstText(
      claims.given_name,
      claims.givenName,
      getPath(fallbackClaims, 'given_name'),
      getPath(fallbackClaims, 'givenName'),
      getPath(fallbackClaims, 'firstName'),
    ),
    familyName: firstText(
      claims.family_name,
      claims.familyName,
      getPath(fallbackClaims, 'family_name'),
      getPath(fallbackClaims, 'familyName'),
      getPath(fallbackClaims, 'lastName'),
    ),
    tenantId,
    tenantCode,
    actorId: firstText(
      claims.operations_user_id,
      claims['custom:operations_user_id'],
      getPath(fallbackClaims, 'operations_user_id'),
      getPath(fallbackClaims, 'custom:operations_user_id'),
      getPath(fallbackClaims, 'operationsUserId'),
      claims.sub,
      fallbackUsername,
    ),
    actorRole: primaryRole(actorRoles) || 'OPS_MANAGER',
    actorRoles: actorRoles.length ? actorRoles : ['OPS_MANAGER'],
  };
}

export function rolesFromClaims(
  claims: Record<string, any> | null,
  fallbackClaims?: unknown,
): string[] {
  const raw: string[] = [];
  const groups = claims?.groups ?? claims?.['cognito:groups'];
  if (Array.isArray(groups)) {
    raw.push(...groups.map(String));
  }
  if (typeof groups === 'string') {
    raw.push(...groups.split(','));
  }
  const roles = claims?.['custom:roles'] ?? claims?.roles ?? claims?.roleCodes;
  if (Array.isArray(roles)) {
    raw.push(...roles.map(String));
  }
  if (typeof roles === 'string') {
    raw.push(...roles.split(','));
  }
  const fallbackRoles = firstText(
    getPath(fallbackClaims, 'custom:roles'),
    getPath(fallbackClaims, 'roles'),
    getPath(fallbackClaims, 'roleCodes'),
  );
  if (fallbackRoles) {
    raw.push(...fallbackRoles.split(','));
  }
  return raw
    .map(normalizeRoleClaim)
    .filter(Boolean)
    .filter((role, index, all) => all.indexOf(role) === index);
}

export function normalizeRoleClaim(value: string): string {
  const role =
    value
      .trim()
      .split('__')
      .pop()
      ?.trim()
      .toUpperCase()
      .replace(/[\s-]+/g, '_') ?? '';
  return role;
}

export function primaryRole(roles: string[]): string {
  if (roles.includes('ROOT')) return 'ROOT';
  if (roles.includes('ADMIN')) return 'ADMIN';
  if (roles.includes('FUND_MANAGER')) return 'FUND_MANAGER';
  if (roles.includes('FUND_OPERATIONS')) return 'FUND_OPERATIONS';
  if (roles.includes('FUND_VIEWER')) return 'FUND_VIEWER';
  if (roles.includes('OPS_MANAGER')) return 'OPS_MANAGER';
  if (roles.includes('OPERATIONS_MANAGER')) return 'OPERATIONS_MANAGER';
  return roles[0] ?? '';
}

function firstRecord(...values: unknown[]): Record<string, unknown> {
  for (const value of values) {
    const record = asRecord(value);
    if (Object.keys(record).length > 0) {
      return record;
    }
  }
  return {};
}

function firstList(...values: unknown[]): Record<string, unknown>[] {
  for (const value of values) {
    const list = Array.isArray(value)
      ? value.map(asRecord).filter((record) => Object.keys(record).length > 0)
      : [];
    if (list.length > 0) {
      return list;
    }
    const record = asRecord(value);
    if (Object.keys(record).length > 0) {
      return [record];
    }
  }
  return [];
}

function firstBoolean(...values: unknown[]): boolean | undefined {
  for (const value of values) {
    if (typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'string' && value.trim()) {
      return value.toLowerCase() === 'true';
    }
  }
  return undefined;
}

function firstAccountText<T extends object>(records: T[], key: string): string {
  return firstText(...records.map((record) => getPath(record, key)));
}

export function firstText(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
}

export function stringArrayValue(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map(String).filter(Boolean);
  }
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
}

export function joinedText(...values: unknown[]): string {
  return values
    .flatMap((value) => stringArrayValue(value))
    .filter(Boolean)
    .join(', ');
}

export function firstFromKeys(source: unknown, keys: string[]): string {
  return firstText(...keys.map((key) => getPath(source, key)));
}

export function firstValueFromKeys(source: unknown, keys: string[]): unknown {
  for (const key of keys) {
    const value = getPath(source, key);
    if (value != null && value !== '') {
      return value;
    }
  }
  return undefined;
}

export function asRecord(source: unknown): Record<string, unknown> {
  return source && typeof source === 'object' && !Array.isArray(source)
    ? (source as Record<string, unknown>)
    : {};
}

export function moneyText(amount: unknown, currency: unknown = 'GBP'): string {
  const numeric = typeof amount === 'number' ? amount : Number(amount);
  if (!Number.isFinite(numeric)) {
    return '-';
  }
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: firstText(currency, 'GBP'),
    maximumFractionDigits: 2,
  }).format(numeric);
}

export function getPath(source: unknown, path: string): unknown {
  return path.split('.').reduce<unknown>((current, key) => {
    if (current == null) {
      return undefined;
    }
    if (Array.isArray(current)) {
      return current[Number(key)];
    }
    if (typeof current === 'object') {
      return (current as Record<string, unknown>)[key];
    }
    return undefined;
  }, source);
}

export function normalisePage<T>(data: unknown): PageResponse<T> {
  const items = normaliseList<T>(
    getPath(data, 'items') ??
      getPath(data, 'responseItems') ??
      getPath(data, 'authorisationList') ??
      data,
  );
  return {
    items,
    total: Number(
      getPath(data, 'total') ?? getPath(data, 'totalRecords') ?? items.length,
    ),
    pageIndex: Number(
      getPath(data, 'pageIndex') ??
        Math.max(Number(getPath(data, 'pageNo') ?? 1) - 1, 0),
    ),
    pageSize: Number(
      getPath(data, 'pageSize') ??
        getPath(data, 'recordsPerPage') ??
        items.length,
    ),
  };
}

export function normaliseList<T>(data: unknown): T[] {
  if (Array.isArray(data)) {
    return data as T[];
  }
  if (Array.isArray(getPath(data, 'items'))) {
    return getPath(data, 'items') as T[];
  }
  if (Array.isArray(getPath(data, 'responseItems'))) {
    return getPath(data, 'responseItems') as T[];
  }
  if (Array.isArray(getPath(data, 'authorisationList'))) {
    return getPath(data, 'authorisationList') as T[];
  }
  return [];
}

export function holdingsForAccount(
  accountId: string,
  snapshotHoldings: unknown,
): HoldingView[] {
  if (!Array.isArray(snapshotHoldings)) {
    return [];
  }
  return (
    (snapshotHoldings as AccountHoldingsView[]).find(
      (group) => group.accountId === accountId,
    )?.items ?? []
  );
}

export const accountOpenedDateKeys = [
  'openedAt',
  'openedDate',
  'openingDate',
  'accountOpeningDate',
  'createdAt',
  'startDate',
];
export const accountClosedDateKeys = [
  'closedDate',
  'closedAt',
  'closingDate',
  'accountClosingDate',
  'closureDate',
  'terminatedAt',
  'endDate',
];

export function accountDateFromSources(
  account: Record<string, unknown>,
  relatedAccounts: Record<string, unknown>[],
  dateKeys: string[],
): string {
  const directDate = firstFromKeys(account, dateKeys);
  if (directDate) {
    return directDate;
  }

  const accountId = firstFromKeys(account, ['accountId', 'id']);
  if (!accountId) {
    return '';
  }

  return firstText(
    ...relatedAccounts
      .filter(
        (candidate) =>
          firstFromKeys(candidate, ['accountId', 'id']) === accountId,
      )
      .map((candidate) => firstFromKeys(candidate, dateKeys)),
  );
}

export function compactParams(params: Record<string, string>) {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value.trim()),
  );
}

export function caseFilterParams(filters: {
  q: string;
  status: string;
  role: string;
}): Record<string, string> {
  const params = compactParams({ q: filters.q, role: filters.role });
  if (filters.status === 'OPEN_CASES') {
    return { ...params, statusGroup: 'OPEN' };
  }
  if (filters.status === 'CLOSED_CASES') {
    return { ...params, statusGroup: 'CLOSED' };
  }
  return filters.status ? { ...params, status: filters.status } : params;
}

export function errorMessage(error: unknown): string {
  const responseMessage =
    getPath(error, 'response.data.message') ??
    getPath(error, 'response.data.error') ??
    getPath(error, 'message');
  return typeof responseMessage === 'string'
    ? responseMessage
    : 'The operations API did not return successfully.';
}

export function fullName(user: TenantUser): string {
  return [user.givenName, user.familyName].filter(Boolean).join(' ');
}

export function labelize(value: string): string {
  const acronyms = new Map([
    ['Ai', 'AI'],
    ['Aml', 'AML'],
    ['Api', 'API'],
    ['Id', 'ID'],
    ['Idv', 'IDV'],
    ['Kyb', 'KYB'],
    ['Kyc', 'KYC'],
    ['Sla', 'SLA'],
    ['Uk', 'UK'],
  ]);

  return value
    .replace(/_/g, ' ')
    .replace(/\bhuman\b/gi, 'operations')
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
    .replace(/\b[A-Za-z]+\b/g, (word) => acronyms.get(word) ?? word);
}

export function shortId(value?: string): string {
  return value ? `${value.slice(0, 8)}...` : '-';
}

export function looksLikeUuid(value?: string): boolean {
  if (!value) {
    return false;
  }
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    value.trim(),
  );
}

export function preferredReference(...values: unknown[]): string {
  const strings = values
    .filter(
      (value): value is string =>
        typeof value === 'string' && value.trim().length > 0,
    )
    .map((value) => value.trim());
  return strings.find((value) => !looksLikeUuid(value)) ?? strings[0] ?? '';
}

export function compactDate(value?: string): string {
  if (!value) {
    return '-';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}
